/**
 * SDK custom parameters — operator UI (feat #205).
 *
 * Composition:
 *   - Gradient emerald->sky header.
 *   - Mapping card: 10 rows, one per raw paramN slot, with a snake_case
 *     label input + inline validation (char class, reserved words,
 *     uniqueness). "Save mapping" commits the PUT.
 *   - Recent events card: shows the server-side labeled events (with raw
 *     paramN fallback when a slot has no label).
 *
 * Brand: emerald->sky headline, zinc borders, 6-8px rounded, light only,
 * no emojis, no competitor names.
 */
import { useEffect, useMemo, useState, type FormEvent } from "react";
import {
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import {
	formatUnixSec,
	getMapping,
	listEvents,
	PARAM_KEYS,
	resolveAllLabels,
	sdkCustomParamsQueryKeys,
	updateMapping,
	validateMappingLocally,
	type ListEventsResponse,
	type MappingValidationIssue,
	type ParamKey,
} from "./api";

// ---------------------------------------------------------------------------
// Style tokens.
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20";
const INPUT_ERROR =
	"w-full rounded-[6px] border border-rose-400 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-rose-500 focus:outline-none focus:ring-2 focus:ring-rose-500/20";
const BTN_PRIMARY =
	"rounded-[6px] border border-emerald-600 bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-emerald-700 hover:border-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_NEUTRAL =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const HINT = "text-xs text-zinc-500";

// ---------------------------------------------------------------------------
// Utility — render human-readable issue text for a local validation entry.
// ---------------------------------------------------------------------------

function reasonText(reason: MappingValidationIssue["reason"]): string {
	switch (reason) {
		case "too_long":
			return "Too long (max 30).";
		case "invalid_format":
			return "Must be snake_case (a-z, 0-9, _) starting with a letter.";
		case "reserved_word":
			return "This name is reserved.";
		case "duplicate_label":
			return "Duplicate label across slots.";
		case "empty":
			return "Empty.";
		default:
			return reason;
	}
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function SdkCustomParamsPage() {
	const queryClient = useQueryClient();

	const mappingQuery = useQuery({
		queryKey: sdkCustomParamsQueryKeys.mapping(),
		queryFn: getMapping,
	});

	const eventsQuery = useQuery<ListEventsResponse>({
		queryKey: sdkCustomParamsQueryKeys.events(50),
		queryFn: () => listEvents(50),
	});

	const [draft, setDraft] = useState<Partial<Record<ParamKey, string>>>({});

	// Seed the draft once the server mapping loads.
	useEffect(() => {
		const m = mappingQuery.data;
		if (!m) return;
		const initial: Partial<Record<ParamKey, string>> = {};
		for (const k of PARAM_KEYS) {
			const v = m[k];
			if (typeof v === "string") initial[k] = v;
			else initial[k] = "";
		}
		setDraft(initial);
	}, [mappingQuery.data]);

	const issues = useMemo(() => validateMappingLocally(draft), [draft]);
	const hasIssues = issues.length > 0;

	const updateMutation = useMutation({
		mutationFn: updateMapping,
		onSuccess: () => {
			toast.success("Mapping saved.");
			queryClient.invalidateQueries({
				queryKey: sdkCustomParamsQueryKeys.all,
			});
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to save mapping.");
		},
	});

	const onSave = (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		if (hasIssues) {
			toast.error("Fix validation issues before saving.");
			return;
		}
		// Emit only populated slots; empty string = clear slot.
		const patch: Partial<Record<ParamKey, string | null>> = {};
		for (const k of PARAM_KEYS) {
			const v = (draft[k] ?? "").trim();
			patch[k] = v.length > 0 ? v : "";
		}
		updateMutation.mutate(patch);
	};

	return (
		<main
			data-testid="sdk-custom-params-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10 sm:py-14"
		>
			<Header />

			<MappingCard
				draft={draft}
				setDraft={setDraft}
				issues={issues}
				onSave={onSave}
				onReset={() => {
					const m = mappingQuery.data;
					if (!m) return;
					const reset: Partial<Record<ParamKey, string>> = {};
					for (const k of PARAM_KEYS) {
						const v = m[k];
						reset[k] = typeof v === "string" ? v : "";
					}
					setDraft(reset);
				}}
				busy={updateMutation.isPending}
				loading={mappingQuery.isLoading}
				error={mappingQuery.error?.message ?? null}
			/>

			<EventsCard
				data={eventsQuery.data ?? null}
				loading={eventsQuery.isLoading}
				error={eventsQuery.error?.message ?? null}
			/>
		</main>
	);
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

function Header() {
	return (
		<header className="flex flex-col gap-2">
			<h1 className="bg-gradient-to-r from-emerald-600 to-sky-500 bg-clip-text text-3xl font-semibold text-transparent sm:text-4xl">
				SDK custom parameters
			</h1>
			<p className="text-sm text-zinc-600">
				Map raw SDK slots (param1 through param10) to human-readable labels.
				Labels are snake_case, 30 chars max, and must be unique across slots.
			</p>
		</header>
	);
}

// ---------------------------------------------------------------------------
// Mapping card
// ---------------------------------------------------------------------------

interface MappingCardProps {
	draft: Partial<Record<ParamKey, string>>;
	setDraft: (next: Partial<Record<ParamKey, string>>) => void;
	issues: MappingValidationIssue[];
	onSave: (e: FormEvent<HTMLFormElement>) => void;
	onReset: () => void;
	busy: boolean;
	loading: boolean;
	error: string | null;
}

function MappingCard(props: MappingCardProps) {
	const issuesBySlot = useMemo(() => {
		const m = new Map<ParamKey, MappingValidationIssue[]>();
		for (const issue of props.issues) {
			const arr = m.get(issue.slot) ?? [];
			arr.push(issue);
			m.set(issue.slot, arr);
		}
		return m;
	}, [props.issues]);

	return (
		<section className={`${CARD} p-6`} aria-label="Parameter label mapping">
			<h2 className="text-lg font-semibold text-zinc-900">Label mapping</h2>
			<p className={HINT}>
				Labels appear as column headers in reports. Leave blank to show the
				raw <code className="font-mono">paramN</code> slot instead.
			</p>

			{props.loading ? (
				<p className="mt-4 text-sm text-zinc-500">Loading mapping…</p>
			) : props.error ? (
				<p className="mt-4 text-sm text-rose-600">{props.error}</p>
			) : (
				<form onSubmit={props.onSave} className="mt-5 flex flex-col gap-3">
					<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
						{PARAM_KEYS.map((slot) => {
							const slotIssues = issuesBySlot.get(slot) ?? [];
							const hasErr = slotIssues.length > 0;
							const inputId = `sdk-param-${slot}`;
							return (
								<div key={slot} className="flex flex-col gap-1">
									<label htmlFor={inputId} className={LABEL}>
										<code className="font-mono text-zinc-900">{slot}</code>
									</label>
									<input
										id={inputId}
										name={slot}
										type="text"
										value={props.draft[slot] ?? ""}
										onChange={(e) => {
											props.setDraft({
												...props.draft,
												[slot]: e.currentTarget.value,
											});
										}}
										placeholder="e.g. item_id"
										className={hasErr ? INPUT_ERROR : INPUT}
										aria-invalid={hasErr}
										aria-describedby={
											hasErr ? `${inputId}-err` : undefined
										}
										data-testid={`input-${slot}`}
									/>
									{hasErr ? (
										<p
											id={`${inputId}-err`}
											role="alert"
											className="text-xs text-rose-600"
										>
											{reasonText(slotIssues[0].reason)}
										</p>
									) : null}
								</div>
							);
						})}
					</div>

					<div className="mt-3 flex items-center justify-end gap-2">
						<button
							type="button"
							className={BTN_NEUTRAL}
							onClick={props.onReset}
							disabled={props.busy}
						>
							Reset
						</button>
						<button
							type="submit"
							className={BTN_PRIMARY}
							disabled={props.busy || props.issues.length > 0}
							data-testid="save-mapping"
						>
							{props.busy ? "Saving…" : "Save mapping"}
						</button>
					</div>
				</form>
			)}
		</section>
	);
}

// ---------------------------------------------------------------------------
// Events card
// ---------------------------------------------------------------------------

interface EventsCardProps {
	data: ListEventsResponse | null;
	loading: boolean;
	error: string | null;
}

function EventsCard(props: EventsCardProps) {
	const columns = useMemo(() => {
		return resolveAllLabels(props.data?.mapping ?? null);
	}, [props.data]);

	return (
		<section className={`${CARD} p-6`} aria-label="Recent SDK events">
			<h2 className="text-lg font-semibold text-zinc-900">Recent events</h2>
			<p className={HINT}>
				Latest 50 SDK events. Columns show your labels when set, or the raw{" "}
				<code className="font-mono">paramN</code> slot otherwise.
			</p>

			{props.loading ? (
				<p className="mt-4 text-sm text-zinc-500">Loading events…</p>
			) : props.error ? (
				<p className="mt-4 text-sm text-rose-600">{props.error}</p>
			) : !props.data || props.data.events.length === 0 ? (
				<p className="mt-4 text-sm text-zinc-500">No events recorded yet.</p>
			) : (
				<div className="mt-4 overflow-x-auto">
					<table
						className="w-full border-collapse text-sm"
						data-testid="events-table"
					>
						<thead>
							<tr className="border-b border-zinc-200 text-left">
								<th className="py-2 pr-3 font-medium text-zinc-600">
									Event
								</th>
								<th className="py-2 pr-3 font-medium text-zinc-600">
									Time
								</th>
								{columns.map((col) => (
									<th
										key={col.slot}
										className="py-2 pr-3 font-medium text-zinc-600"
									>
										<span className={col.mapped ? "" : "text-zinc-400"}>
											{col.label}
										</span>
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							{props.data.events.map((ev) => (
								<tr
									key={ev.id}
									className="border-b border-zinc-100 align-top"
								>
									<td className="py-2 pr-3 font-mono text-xs text-zinc-800">
										{ev.event_name}
									</td>
									<td className="py-2 pr-3 text-xs text-zinc-500">
										{formatUnixSec(ev.created_at)}
									</td>
									{columns.map((col) => {
										const value = ev[col.slot];
										return (
											<td
												key={col.slot}
												className="py-2 pr-3 text-xs text-zinc-800"
											>
												{value === null || value === undefined
													? "—"
													: value}
											</td>
										);
									})}
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</section>
	);
}

export default SdkCustomParamsPage;
